import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { isValidPhoneNumber } from 'libphonenumber-js';

const APPLICATIONS_API_URL =
  process.env.REACT_APP_APPLICATIONS_API_URL || 'https://applications.thepioneers.ai';
const ROLES_CATALOG_URL =
  process.env.REACT_APP_ROLES_CATALOG_URL || 'https://applicants.thepioneers.ai/website/roles-catalog';
const US_STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
]);

const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
const isValidPhone = (countryCode, phoneNumber) => {
  const nationalNumber = phoneNumber.trim();
  return nationalNumber.length === 10 && isValidPhoneNumber(`${countryCode.trim()}${nationalNumber}`, 'US');
};
const isValidLocation = location => {
  const normalizedLocation = location.trim();
  if (!normalizedLocation) return true;

  const match = normalizedLocation.match(/^([^,]+), ([A-Z]{2}), US$/);
  return !!match && match[1] === match[1].trim() && US_STATE_CODES.has(match[2]);
};

const JobApplicationForm = ({ job, className }) => {
  const formId = job?.vms_job_id || job?.job_id || job?.id || 'job';
  const applicationJobId = job?.vms_job_id || job?.job_id || job?.id;
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    location: '',
    countryCode: '+1',
    phoneNumber: '',
    role: job?.role || '',
    specialty: job?.specialty || '',
  });
  const [rolesCatalog, setRolesCatalog] = useState([]);
  const [isCatalogLoading, setIsCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [touchedFields, setTouchedFields] = useState({ email: false, phoneNumber: false });

  useEffect(() => {
    const controller = new AbortController();

    const getRolesCatalog = async () => {
      setIsCatalogLoading(true);
      setCatalogError('');

      try {
        const response = await fetch(ROLES_CATALOG_URL, { signal: controller.signal });
        if (!response.ok) throw new Error(`Failed to fetch roles catalog: ${response.status}`);

        const result = await response.json();
        if (!Array.isArray(result.Items)) throw new Error('Roles catalog has an invalid response format');

        setRolesCatalog(result.Items);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Error fetching roles catalog:', error);
        setCatalogError('Roles and specialties are temporarily unavailable. Please try again later.');
      } finally {
        if (!controller.signal.aborted) setIsCatalogLoading(false);
      }
    };

    getRolesCatalog();

    return () => controller.abort();
  }, []);

  const selectedRole = useMemo(
    () => rolesCatalog.find(role => role.code === form.role),
    [form.role, rolesCatalog],
  );

  const specialtyOptions = selectedRole?.specialties || [];
  const isEmailInvalid = !!form.email.trim() && !isValidEmail(form.email);
  const isPhoneInvalid =
    !!form.phoneNumber.trim() && !isValidPhone(form.countryCode, form.phoneNumber);

  const handleChange = (name, value) => {
    setSubmitError('');
    setForm(previousForm => ({ ...previousForm, [name]: value }));
  };

  const handleRoleChange = value => {
    setSubmitError('');
    setForm(previousForm => ({
      ...previousForm,
      role: value,
      specialty: '',
    }));
  };

  const setFieldTouched = (name, isTouched) => {
    setTouchedFields(previousFields => ({ ...previousFields, [name]: isTouched }));
  };

  const isDisabled =
    !form.firstName.trim() ||
    !form.lastName.trim() ||
    !applicationJobId ||
    !form.email.trim() ||
    !isValidEmail(form.email) ||
    !isValidLocation(form.location) ||
    !form.countryCode.trim() ||
    !form.phoneNumber.trim() ||
    !isValidPhone(form.countryCode, form.phoneNumber) ||
    !form.role ||
    (specialtyOptions.length > 0 && !form.specialty) ||
    isCatalogLoading ||
    !!catalogError ||
    isSending;

  const handleSubmit = async event => {
    event.preventDefault();
    if (isDisabled) return;

    setIsSending(true);
    setSubmitError('');

    const phone = `${form.countryCode.trim()}${form.phoneNumber.trim()}`;
    const jobReference = job?.vms_job_id
      ? { vms_job_id: job.vms_job_id }
      : { job_id: job?.job_id || job?.id };
    const location = form.location.trim();

    try {
      const response = await fetch(`${APPLICATIONS_API_URL}/website/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...jobReference,
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          email: form.email.trim(),
          phone,
          role: form.role,
          specialties: form.specialty ? [form.specialty] : [],
          ...(location ? { location } : {}),
        }),
      });

      if (!response.ok) throw new Error(`Failed to submit application: ${response.status}`);

      setIsSuccess(true);
    } catch (error) {
      console.error('Error submitting job application:', error);
      setSubmitError('We could not submit your application. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <form className={cn('jobApplicationForm', className)} onSubmit={handleSubmit}>
      {isSuccess ? (
        <div className='jobApplicationSuccess' role='status'>
          <svg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 120 120' fill='none'>
            <path
              d='M45 60L55 70L75 50M110 60C110 87.6142 87.6142 110 60 110C32.3858 110 10 87.6142 10 60C10 32.3858 32.3858 10 60 10C87.6142 10 110 32.3858 110 60Z'
              stroke='#14A852'
              strokeWidth='5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
          <span className='jobApplicationSuccessTitle'>Application Submitted!</span>
          <span className='jobApplicationSuccessText'>Thank you. A recruiter will contact you with the next steps.</span>
        </div>
      ) : (
        <>
          <div className='jobApplicationHeader'>
            <span className='jobApplicationTitle'>Apply for this position</span>
            {job?.title && <span className='jobApplicationSubtitle'>{job.title}</span>}
          </div>

          <div className='jobApplicationFields'>
            <div className='jobApplicationField'>
              <label className='jobApplicationLabel' htmlFor={`first-name-${formId}`}>
                First Name <span className='mainFormRequiredMark'>*</span>
              </label>
              <input
                id={`first-name-${formId}`}
                className='jobApplicationInput'
                placeholder='First name'
                autoComplete='given-name'
                required
                value={form.firstName}
                onChange={event => handleChange('firstName', event.target.value)}
              />
            </div>

            <div className='jobApplicationField'>
              <label className='jobApplicationLabel' htmlFor={`last-name-${formId}`}>
                Last Name <span className='mainFormRequiredMark'>*</span>
              </label>
              <input
                id={`last-name-${formId}`}
                className='jobApplicationInput'
                placeholder='Last name'
                autoComplete='family-name'
                required
                value={form.lastName}
                onChange={event => handleChange('lastName', event.target.value)}
              />
            </div>

            <div className='jobApplicationField'>
              <label className='jobApplicationLabel' htmlFor={`email-${formId}`}>
                Email <span className='mainFormRequiredMark'>*</span>
              </label>
              <input
                id={`email-${formId}`}
                className='jobApplicationInput'
                type='email'
                placeholder='Email address'
                autoComplete='email'
                required
                aria-invalid={touchedFields.email && isEmailInvalid}
                aria-describedby={
                  touchedFields.email && isEmailInvalid ? `email-error-${formId}` : undefined
                }
                value={form.email}
                onChange={event => handleChange('email', event.target.value)}
                onFocus={() => setFieldTouched('email', false)}
                onBlur={() => setFieldTouched('email', true)}
              />
              {touchedFields.email && isEmailInvalid && (
                <span className='jobApplicationFieldError' id={`email-error-${formId}`} role='alert'>
                  Enter a valid email address.
                </span>
              )}
            </div>

            <div className='jobApplicationField'>
              <label className='jobApplicationLabel' htmlFor={`location-${formId}`}>Location</label>
              <input
                id={`location-${formId}`}
                className='jobApplicationInput'
                placeholder='City, ST, US'
                autoComplete='address-level2'
                aria-invalid={!!form.location.trim() && !isValidLocation(form.location)}
                aria-describedby={`location-error-${formId}`}
                value={form.location}
                onChange={event => handleChange('location', event.target.value)}
              />
              {!!form.location.trim() && !isValidLocation(form.location) && (
                <span className='jobApplicationFieldError' id={`location-error-${formId}`} role='alert'>
                  Use the format City, ST, US (for example: Austin, TX, US).
                </span>
              )}
            </div>

            <fieldset className='jobApplicationPhone' aria-label='Phone'>
              <div className='jobApplicationPhoneFields'>
                <div className='jobApplicationField jobApplicationCountryCode'>
                  <label className='jobApplicationSmallLabel' htmlFor={`country-code-${formId}`}>Country Code</label>
                  <input
                    id={`country-code-${formId}`}
                    className='jobApplicationInput'
                    type='tel'
                    inputMode='tel'
                    placeholder='+1'
                    autoComplete='tel-country-code'
                    disabled
                    value={form.countryCode}
                  />
                </div>
                <div className='jobApplicationField jobApplicationPhoneNumber'>
                  <label className='jobApplicationSmallLabel' htmlFor={`phone-${formId}`}>
                    Phone Number <span className='mainFormRequiredMark'>*</span>
                  </label>
                  <input
                    id={`phone-${formId}`}
                    className='jobApplicationInput'
                    type='tel'
                    inputMode='numeric'
                    autoComplete='tel-national'
                    maxLength={10}
                    required
                    aria-invalid={touchedFields.phoneNumber && isPhoneInvalid}
                    aria-describedby={
                      touchedFields.phoneNumber && isPhoneInvalid
                        ? `phone-error-${formId}`
                        : undefined
                    }
                    value={form.phoneNumber}
                    onChange={event => handleChange('phoneNumber', event.target.value.replace(/[^0-9]/g, ''))}
                    onFocus={() => setFieldTouched('phoneNumber', false)}
                    onBlur={() => setFieldTouched('phoneNumber', true)}
                  />
                  {touchedFields.phoneNumber && isPhoneInvalid && (
                    <span className='jobApplicationFieldError' id={`phone-error-${formId}`} role='alert'>
                      Enter a valid 10-digit US phone number.
                    </span>
                  )}
                </div>
              </div>
            </fieldset>

            <div className='jobApplicationField'>
              <label className='jobApplicationLabel' htmlFor={`role-${formId}`}>
                Role <span className='mainFormRequiredMark'>*</span>
              </label>
              <select
                id={`role-${formId}`}
                className='jobApplicationInput jobApplicationSelect'
                value={form.role}
                required
                disabled={isCatalogLoading || !!catalogError}
                onChange={event => handleRoleChange(event.target.value)}
              >
                <option value=''>
                  {isCatalogLoading ? 'Loading roles…' : catalogError ? 'Roles unavailable' : 'Select role'}
                </option>
                {rolesCatalog.map(role => <option value={role.code} key={role.code}>{role.label}</option>)}
              </select>
            </div>

            <div className='jobApplicationField'>
              <label className='jobApplicationLabel' htmlFor={`specialty-${formId}`}>
                Specialties
                {!!specialtyOptions.length && <span className='mainFormRequiredMark'> *</span>}
              </label>
              <select
                id={`specialty-${formId}`}
                className='jobApplicationInput jobApplicationSelect'
                value={form.specialty}
                required={!!specialtyOptions.length}
                disabled={isCatalogLoading || !form.role || !specialtyOptions.length}
                onChange={event => handleChange('specialty', event.target.value)}
              >
                <option value=''>
                  {!form.role
                    ? 'Select role first'
                    : specialtyOptions.length
                    ? 'Select specialty'
                    : 'No specialties available'}
                </option>
                {specialtyOptions.map(specialty => (
                  <option value={specialty.code} key={specialty.code}>{specialty.label}</option>
                ))}
              </select>
            </div>
          </div>

          {catalogError && <span className='jobApplicationCatalogError' role='alert'>{catalogError}</span>}
          {submitError && <span className='jobApplicationError' role='alert'>{submitError}</span>}
          <button className='jobApplicationSubmit' type='submit' disabled={isDisabled}>
            {isSending ? 'Submitting…' : 'Submit Application'}
          </button>
        </>
      )}
    </form>
  );
};

export default JobApplicationForm;
